import { useCallback, useRef } from "react"
import { Alert, Box, CircularProgress, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useAnalytics } from "connected-components/analytics"
import { ERROR } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import { useAiSettings, useSetNetworkAccess, useSetReadOnly } from "resources/ai-settings"
import type { AiSettingsResponse, NetworkAccessChange, NetworkAccessSaved } from "resources/ai-settings"
import { KellyPanePage } from "../kelly-pane-page"
import type { KellyPaneLayout } from "../kelly-pane-page"
import { ADMIN_SETTINGS_EVENTS } from "./events"
import { NetworkAccessCard } from "./network-access-card"
import { OmegaPointAccessCard } from "./omega-point-access-card"

const BODY_SX = { flex: 1, minHeight: 0, overflowY: "auto", p: 2 } satisfies SxProps<Theme>
const WIDE_BODY_SX = { ...BODY_SX, py: 3 } satisfies SxProps<Theme>
const COLUMN_SX = { maxWidth: 640, mx: "auto", display: "flex", flexDirection: "column", gap: 2 } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>

// A firm's allowed domains can name its vendors and internal hosts, so analytics only gets the counts.
function getSettingChangedProperties(saved: NetworkAccessSaved) {
  switch (saved.setting) {
    case "webSearch": return { setting: saved.setting, oldValue: saved.oldValue, newValue: saved.newValue }
    case "allowedHosts": return {
      setting: saved.setting,
      addedCount: saved.newValue.filter((host) => !saved.oldValue.includes(host)).length,
      removedCount: saved.oldValue.filter((host) => !saved.newValue.includes(host)).length,
    }
  }
}

interface AdminSettingsCardSlotProps {
  status: DataStatus
  data: AiSettingsResponse | undefined
  isSaving: boolean
  isSavingNetwork: boolean
  onChange: (to: boolean) => void
  onNetworkChange: (change: NetworkAccessChange) => Promise<boolean>
}

function AdminSettingsCardSlot({ status, data, isSaving, isSavingNetwork, onChange, onNetworkChange }: AdminSettingsCardSlotProps) {
  if (status === ERROR && !data) {
    return <Alert severity="error" data-testid="admin-settings-error">Couldn&apos;t load settings. Please try again later.</Alert>
  }
  if (!data) {
    return <Box sx={LOADING_SX}><CircularProgress size={20} aria-label="Loading settings" /></Box>
  }
  const { readOnly, ...networkSettings } = data
  // A failed background refetch keeps the cached data, so the card stays under a warning.
  return (
    <>
      {status === ERROR && (
        <Alert severity="warning" data-testid="admin-settings-stale">Couldn&apos;t refresh settings. The value shown may be out of date.</Alert>
      )}
      <OmegaPointAccessCard
        readOnly={readOnly}
        isSaving={isSaving}
        onChange={onChange}
      />
      <NetworkAccessCard
        settings={networkSettings}
        isSaving={isSavingNetwork}
        onChange={onNetworkChange}
      />
    </>
  )
}

interface AdminSettingsPageProps {
  layout: KellyPaneLayout
}

export function AdminSettingsPage({ layout }: AdminSettingsPageProps) {
  const { status, data } = useAiSettings()
  const { setReadOnly, isSaving } = useSetReadOnly()
  const { setNetworkAccess, isSaving: isSavingNetwork } = useSetNetworkAccess()
  const { track } = useAnalytics()
  // isSaving reaches React one notify tick after the click, so a second click can land before it.
  const isInFlight = useRef(false)

  // The hook reverts the cache and shows the toast on failure; the rejection carries nothing more to handle.
  const onChange = useCallback((to: boolean) => {
    if (isInFlight.current) return
    isInFlight.current = true
    void setReadOnly(to).then(
      ({ oldValue, newValue }) => { track(ADMIN_SETTINGS_EVENTS.SETTING_CHANGED, { setting: "readOnly", oldValue, newValue }) },
      () => undefined,
    ).finally(() => { isInFlight.current = false })
  }, [setReadOnly, track])

  // The hook guards against overlapping network saves and resolves null for a dropped one.
  const saveNetworkAccess = useCallback((change: NetworkAccessChange) => {
    return setNetworkAccess(change).then(
      (saved) => {
        if (saved) track(ADMIN_SETTINGS_EVENTS.SETTING_CHANGED, getSettingChangedProperties(saved))
        return saved !== null
      },
      () => false,
    )
  }, [setNetworkAccess, track])

  return (
    <KellyPanePage title="Settings" testId="kelly-admin-settings-page" refocusKey={data ? data.allowedHosts.join(",") : undefined}>
      <Box sx={layout === "two-column" ? WIDE_BODY_SX : BODY_SX}>
        <Box sx={COLUMN_SX}>
          <Typography variant="body2">Choose which capabilities Kelly can use. Settings will apply to all users in this account.</Typography>
          <AdminSettingsCardSlot
            status={status}
            data={data}
            isSaving={isSaving}
            isSavingNetwork={isSavingNetwork}
            onChange={onChange}
            onNetworkChange={saveNetworkAccess}
          />
        </Box>
      </Box>
    </KellyPanePage>
  )
}
